import BodyText, { BODY_TEXT_SIZES } from "@shared/ui/BodyText";
import Label, { LABEL_SIZES } from "@shared/ui/Label";
import moment from "moment-timezone";
import { CaretDown, CaretRight, DotsThree } from "phosphor-react";
import React from "react";
import { useIntl } from "react-intl";

import EventTile from "~/components/preventiveMaintenance/EventTile";
import { HourglassIconSmall } from "~/icons/icons";

const MainTile = ({
  tileVariant,
  title,
  machineName,
  eventId,
  eventDate,
  handleEventPreview,
  containerRef,
  repeatInNumber,
  repeatIn,
  frequency,
  endsOn,
  lastCompletedAt,
  allOccurrencesDone,
  createdBy,
  machineFacilityName,
  machineFacilityId,
  machineId,
  dateFormat,
  isSharedAssetsPage = false,
  showAccordion = false,
  isExpanded = false,
  onToggleExpand = () => {},
  scheduleContent = null,
}: {
  tileVariant: boolean;
  title: string;
  machineName: string;
  eventId: string;
  eventDate: string;
  handleEventPreview: () => {};
  endsOn: string;
  lastCompletedAt: string;
  allOccurrencesDone: boolean;
  containerRef: React.Ref<HTMLElement>;
  repeatInNumber: number;
  repeatIn: string;
  frequency?: string;
  totalProgress: string;
  progressColor: string;
  machineFacilityName: string;
  machineFacilityId: string;
  machineId: string;
  createdBy: { name: string };
  dateFormat: string;
  isSharedAssetsPage?: boolean;
  showAccordion?: boolean;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  scheduleContent?: React.ReactNode;
}) => {
  const {
    messages: { preventiveMaintenance: PM },
  } = useIntl();

  const unitLabel = repeatIn
    ? PM?.frequency?.[repeatIn.toLowerCase()] || repeatIn
    : "";

  const getCollapsedSummary = () => {
    if (allOccurrencesDone) {
      const completedDate = lastCompletedAt
        ? moment(lastCompletedAt).format(dateFormat)
        : "";
      return completedDate
        ? `${PM?.lastCompleted} ${completedDate}`
        : PM?.allOccurrencesDone;
    }

    const isNoRepeat = frequency === "noRepeat" || !repeatInNumber;
    if (isNoRepeat) {
      return PM?.form?.frequencyNoRepeat;
    }

    const endsOnPart = endsOn
      ? (PM?.collapsedEndsOn ?? "Ends on {date}").replace(
          "{date}",
          moment(endsOn).format(dateFormat),
        )
      : PM?.neverEnds ?? "Never ends";

    const everyPart = (PM?.collapsedEvery ?? "Every {count} {unit}")
      .replace("{count}", String(repeatInNumber))
      .replace("{unit}", String(unitLabel).toLowerCase());

    return `${everyPart} • ${endsOnPart}`;
  };

  if (tileVariant) {
    return (
      <EventTile className="flex items-center">
        <EventTile.EventName
          eventName={title}
          machineName={machineName}
          machineFacilityName={machineFacilityName}
          machineFacilityId={machineFacilityId}
          machineId={machineId}
        />
        <div className="flex items-center shrink-0 gap-8 pl-4">
          <EventTile.EvenDetail
            eventId={eventDate}
            label={"Due"}
            isDate={true}
            dateFormat={dateFormat}
          />
          <EventTile.EventCreator creator={createdBy} />
          <div className="flex items-center leading-none hover:cursor-pointer">
            <span onClick={handleEventPreview} ref={containerRef}>
              {/* @ts-ignore */}
              <DotsThree size={20} />
            </span>
          </div>
        </div>
      </EventTile>
    );
  }

  return (
    <div className="border border-solid border-light rounded-lg bg-primary">
      <div
        className={`flex items-center gap-md px-lg py-md ${
          showAccordion ? "cursor-pointer" : ""
        }`}
        onClick={showAccordion ? onToggleExpand : undefined}
        onKeyDown={
          showAccordion
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onToggleExpand();
                }
              }
            : undefined
        }
        role={showAccordion ? "button" : undefined}
        tabIndex={showAccordion ? 0 : undefined}
        aria-expanded={showAccordion ? isExpanded : undefined}
      >
        {showAccordion && (
          <span className="inline-flex items-center justify-center shrink-0 text-secondary pointer-events-none">
            {isExpanded ? <CaretDown size={16} /> : <CaretRight size={16} />}
          </span>
        )}

        <span className="flex shrink-0 items-center justify-center w-2xl h-2xl bg-mint-light rounded-full pointer-events-none">
          {/* @ts-ignore */}
          <HourglassIconSmall />
        </span>

        <div className="flex flex-col flex-1 min-w-0 gap-2xs pointer-events-none">
          <Label size={LABEL_SIZES.LARGE} className="line-clamp-1">
            {title}
          </Label>
          <BodyText
            size={BODY_TEXT_SIZES.X_SMALL}
            color="text-secondary"
            className="line-clamp-1"
          >
            {getCollapsedSummary()}
          </BodyText>
        </div>

        {!isSharedAssetsPage && (
          <span
            className="inline-flex items-center justify-center shrink-0 text-secondary cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              handleEventPreview();
            }}
            ref={containerRef}
          >
            <DotsThree size={20} />
          </span>
        )}
      </div>
      {showAccordion && isExpanded && (
        <div className="px-lg pb-lg">{scheduleContent}</div>
      )}
    </div>
  );
};

export default MainTile;
